import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export const useDashboardList = (params: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, ...params, user_id: user?.id, limitHomeworkPast: 5 };
  const requestUrl = REACT_APP_API_URL + '/dashboard/list';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['dashboard-list'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

export const useTodosUpdate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/todos/update';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { company_id: user?.company_id, ...formPayload, user_id: user?.id };
      return axios.post<any>(`${requestUrl}`, requestParams);
    },
    { onError, onSuccess },
  );
};

export const useInviteCreate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/company/invite/create';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { ...formPayload, user_id: user?.id, company_id: user?.company_id };
      return axios.post<any>(`${requestUrl}`, requestParams);
    },
    { onError, onSuccess },
  );
};

// export const useCampaignLeadList = (onError?: any) => {
//   const { user } = useAppSelector((state) => state.auth);
//   const requestUrl = REACT_APP_API_URL + '/registration/campaign/list';
//   return useMutation(
//     (formPayload: any) => {
//       const requestParams = { ...formPayload, user_id: user?.id, company_id: user?.company_id };
//       return axios.get<any>(`${requestUrl}`, requestParams);
//     },
//     { onError },
//   );
// };

export const useCampaignLeadList = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, user_id: user?.id };
  const requestUrl = REACT_APP_API_URL + '/registration/campaign/list';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['campaign-list'], fetchData, {
    onError,
    refetchOnWindowFocus: false,
  });
};

export const useCampaignAddNote = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/registration/campaign/addNote';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { ...formPayload, user_name: user?.name, company_id: user?.company_id, user_id: user?.id };
      return axios.post<any>(`${requestUrl}`, requestParams);
    },
    { onError, onSuccess },
  );
};

export const useCampaignAddStatus = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/registration/campaign/addStatus';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { ...formPayload, user_name: user?.name, company_id: user?.company_id, user_id: user?.id };
      return axios.post<any>(`${requestUrl}`, requestParams);
    },
    { onError, onSuccess },
  );
};

// DEP Query - Seri no veya fatura numarası ile DEP kaydı sorgulama
export const useDepQuery = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/devices/dep-query';
  return useMutation(
    (params: { searchValue: string; searchType: 'serial' | 'invoice' }) => {
      return axios.get<any>(`${requestUrl}`, { params });
    },
    { onError, onSuccess },
  );
};

// Major Customer Query - Müşteri kodu veya adı ile major müşteri sorgulama
export const useMajorCustomerQuery = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/crt-customers/query';
  return useMutation(
    (params: { searchValue: string }) => {
      return axios.get<any>(`${requestUrl}`, { params });
    },
    { onError, onSuccess },
  );
};

// Stock Query - Stok kodu ile stok sorgulama (DIA + products + products_on_the_way)
export const useStockQuery = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/products/stock-query';
  return useMutation(
    (params: { searchValue: string }) => {
      return axios.get<any>(`${requestUrl}`, { params });
    },
    { onError, onSuccess },
  );
};

// CRT Discount Rates - Programme'a göre indirim oranları
export const useCrtDiscountRateByProgramme = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/crt-discount-rates/by-programme';
  return useMutation(
    (params: { programme: string }) => {
      return axios.get<any>(`${requestUrl}`, { params });
    },
    { onError, onSuccess },
  );
};

// Bağımsız Stok Rezervasyonu - Dashboard hızlı işlem
export const useStockReservation = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/products/stock-reservation';
  return useMutation(
    (payload: { stock_code: string; quantity: number; valid_until: string; note: string }) => {
      return axios.post<any>(`${requestUrl}`, payload);
    },
    { onError, onSuccess },
  );
};

// Ürün Ara (DIA ERP) - Dashboard için
export const useQuickProductSearch = (search: string) => {
  const requestUrl = REACT_APP_API_URL + '/quotes/search-product';
  return useQuery({
    queryKey: ['quick-product-search', search],
    queryFn: async () => {
      const { data } = await axios.post(`${requestUrl}`, { search });
      return data;
    },
    enabled: search.length >= 2,
  });
};

// Stok Bilgisi (DIA canlı) - Dashboard için
export const fetchQuickStockInfo = async (
  stockCodes: string[],
): Promise<Record<string, { stock_quantity: number | null; idle_days: number | null; reserved_quantity: number; available_quantity: number | null }>> => {
  const { data } = await axios.post(`${REACT_APP_API_URL}/quotes/product-stock-info`, { stock_codes: stockCodes });
  return data;
};

// Dashboard Etkileşim Grafiği
export const useInteractionChart = (range: string, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/customer-communications/dashboard-chart';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: { range } });
    return res?.data || {};
  };
  return useQuery(['interaction-chart', range], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: false,
  });
};
